import { useComparador } from '../context/ComparadorContext';
import { db } from '../services/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import {
  BedDouble,
  Bath,
  Ruler,
  Crop,
  DollarSign,
  MapPin,
  Layers,
  Waves,
  Flame,
  Package,
  Sun,
  LayoutGrid,
  Toilet,
} from 'lucide-react';

// Função para pré-carregar as imagens antes de gerar o PDF
const preloadImages = (urls) => {
  const promises = urls.map((url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () =>
        reject(new Error(`Falha ao carregar a imagem: ${url}`));
      img.src = url;
    });
  });
  return Promise.all(promises);
};

export default function CompararImoveis() {
  const { imoveisSelecionados, limparComparador } = useComparador();
  const [imoveis, setImoveis] = useState([]);
  const navigate = useNavigate();
  const pdfRef = useRef();

  useEffect(() => {
    const buscarImoveis = async () => {
      if (imoveisSelecionados.length === 0) return;

      const ref = collection(db, 'imoveis');
      const q = query(ref, where('__name__', 'in', imoveisSelecionados));
      const snap = await getDocs(q);
      const lista = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      lista.sort((a, b) => (b.preco || 0) - (a.preco || 0));
      setImoveis(lista);
    };

    buscarImoveis();
  }, [imoveisSelecionados]);

  const gerarPDF = async () => {
    if (!pdfRef.current) return;

    try {
      const imageUrls = imoveis
        .map((imovel) => imovel.imagens?.[0]?.url)
        .filter(Boolean);
      if (imageUrls.length > 0) {
        await preloadImages(imageUrls);
        console.log('✅ Todas as imagens foram pré-carregadas.');
      }

      html2pdf(pdfRef.current, {
        margin: 0.5,
        filename: 'comparacao-de-imoveis.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' },
      });
    } catch (error) {
      console.error('❌ Erro ao gerar o PDF:', error);
      alert(
        'Não foi possível gerar o PDF. Verifique o console para mais detalhes.'
      );
    }
  };

  if (imoveis.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600">
          Nenhum imóvel selecionado para comparação.
        </p>
        <button
          onClick={() => navigate('/home')}
          className="mt-4 px-4 py-2 bg-black text-white rounded"
        >
          Voltar
        </button>
      </div>
    );
  }

  const atributos = [
    { label: 'Bairro', icone: MapPin, chave: 'bairro' },
    {
      label: 'Área construída',
      icone: Ruler,
      chave: 'areaConstruida',
      sufixo: 'm²',
    },
    {
      label: 'Área do terreno',
      icone: Crop,
      chave: 'areaTerreno',
      sufixo: 'm²',
    },
    {
      label: 'Preço',
      icone: DollarSign,
      chave: 'preco',
      formatar: (v) => `R$ ${v?.toLocaleString('pt-BR')}`,
    },
    {
      label: 'Valor por m²',
      icone: LayoutGrid,
      chave: 'valorM2',
      calcular: (i) =>
        i.preco && i.areaConstruida ? i.preco / i.areaConstruida : null,
      formatar: (v) => (v ? `R$ ${Math.round(v)}/m²` : '-'),
    },
    { label: 'Quartos', icone: BedDouble, chave: 'quartos' },
    { label: 'Suítes', icone: Bath, chave: 'suites' },
    { label: 'Banheiros', icone: Toilet, chave: 'banheiros' },
    { label: 'Acabamento', icone: Layers, chave: 'acabamento' },
  ];

  const diferenciais = [
    { label: 'Piscina', icone: Waves, chave: 'piscina' },
    { label: 'Churrasqueira', icone: Flame, chave: 'churrasqueira' },
    { label: 'Móveis planejados', icone: Package, chave: 'moveisPlanejados' },
    { label: 'Placa solar', icone: Sun, chave: 'placaSolar' },
  ];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Comparar Imóveis</h1>

      <div className="flex justify-end mb-4 gap-2">
        <button
          onClick={gerarPDF}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Salvar como PDF
        </button>
        <div className="flex justify-end mb-4 gap-2">
          <button
            onClick={gerarPDF}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Salvar como PDF
          </button>

          <button
            onClick={() => {
              limparComparador();
              navigate('/home'); // ✅ Redireciona para tela inicial
            }}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Limpar comparação
          </button>
        </div>
      </div>

      <div ref={pdfRef} className="p-6 bg-gray-50 rounded-lg shadow-inner">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {imoveis.map((imovel) => (
            <div
              key={imovel.id}
              className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200"
            >
              <img
                src={imovel.imagens?.[0]?.url || '/placeholder.jpg'}
                alt={imovel.titulo}
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h2 className="font-bold text-xl mb-1 text-gray-900">
                  {imovel.titulo}
                </h2>
                <p className="text-sm text-gray-600 mb-4">{imovel.bairro}</p>

                <div className="space-y-3">
                  {atributos.map((a) => {
                    const Icon = a.icone;
                    const valor = a.calcular
                      ? a.calcular(imovel)
                      : imovel[a.chave];
                    const texto = a.formatar
                      ? a.formatar(valor)
                      : `${valor ?? '-'} ${a.sufixo || ''}`;

                    return (
                      <div
                        key={a.label}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <Icon className="w-4 h-4 text-gray-500" />
                        <span>
                          {a.label}:{' '}
                          <span className="font-semibold">{texto}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 border-t pt-4">
                  <h3 className="font-semibold text-gray-700 mb-2">
                    Diferenciais
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {diferenciais.map((d) =>
                      imovel[d.chave] ? (
                        <span
                          key={d.label}
                          className="bg-green-100 text-green-700 text-xs font-medium px-2.5 py-0.5 rounded-full"
                        >
                          {d.label}
                        </span>
                      ) : (
                        <span
                          key={d.label}
                          className="bg-gray-100 text-gray-500 text-xs font-medium px-2.5 py-0.5 rounded-full"
                        >
                          {d.label}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Rodapé com a logo e o slogan */}
        <div className="mt-10 pt-4 border-t text-center text-gray-500">
          <img
            src="/elion-logo.png"
            alt="Elion Imóveis"
            className="h-10 mx-auto mb-2"
          />
          <p className="text-sm">Seu parceiro de negócios imobiliários</p>
        </div>
      </div>
    </div>
  );
}
